<template src="./attivazioneAccount.html"></template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "@/composables/UseToast";
import { verifica_attivazione_token, attiva_account } from "@/api/api.auth";

export default {
    name: "AttivazioneAccount",

    setup() {
        const router = useRouter();
        const route = useRoute();
        const toast = useToast();

        // ── Stato pagina: 'verifica' | 'form' | 'errore' | 'successo' ────────
        const stato = ref("verifica");
        const token = ref("");
        const nomeUtente = ref("");

        // ── Campi form ────────────────────────────────────────────────────────
        const formAttivazione = ref(null);
        const password = ref("");
        const confermaPassword = ref("");
        const mostraPassword = ref(false);
        const mostraConferma = ref(false);
        const attesa = ref(false);

        // ── Requisiti visivi password ─────────────────────────────────────────
        const requisitiPassword = computed(() => [
            {
                testo: "Almeno 8 caratteri",
                soddisfatto: password.value.length >= 8,
            },
            {
                testo: "Almeno una lettera maiuscola",
                soddisfatto: /[A-Z]/.test(password.value),
            },
            {
                testo: "Almeno un carattere speciale (!@#$%^&*...)",
                soddisfatto: /[^a-zA-Z0-9]/.test(password.value),
            },
        ]);

        // ── Regole validazione ────────────────────────────────────────────────
        const regolePassword = [
            (v) => !!v || "Campo obbligatorio",
            (v) => v.length >= 8 || "Minimo 8 caratteri",
            (v) => /[A-Z]/.test(v) || "Almeno una lettera maiuscola",
            (v) => /[^a-zA-Z0-9]/.test(v) || "Almeno un carattere speciale",
        ];

        const regoleConferma = [
            (v) => !!v || "Campo obbligatorio",
            (v) => v === password.value || "Le password non coincidono",
        ];

        // ── Verifica token al mount ───────────────────────────────────────────
        onMounted(async () => {
            token.value = route.query.token || "";

            if (!token.value) {
                stato.value = "errore";
                return;
            }

            try {
                const { data } = await verifica_attivazione_token(token.value);
                nomeUtente.value = data?.userName ?? "";
                stato.value = "form";
            } catch {
                stato.value = "errore";
            }
        });

        // ── Submit attivazione ────────────────────────────────────────────────
        const submit = async () => {
            const { valid } = await formAttivazione.value.validate();
            if (!valid) return;

            attesa.value = true;
            try {
                await attiva_account({
                    token: token.value,
                    password: password.value,
                });
                stato.value = "successo";

                // Redirect automatico al login dopo 3 secondi
                setTimeout(() => vaiAlLogin(), 3000);
            } catch {
                toast.error("Errore durante l'attivazione. Il link potrebbe non essere più valido.");
                stato.value = "errore";
            } finally {
                attesa.value = false;
            }
        };

        const vaiAlLogin = () => router.push("/login");

        return {
            stato,
            nomeUtente,
            formAttivazione,
            password,
            confermaPassword,
            mostraPassword,
            mostraConferma,
            attesa,
            requisitiPassword,
            regolePassword,
            regoleConferma,
            submit,
            vaiAlLogin,
        };
    },
};
</script>

<style scoped>
.login-container {
    background: linear-gradient(135deg, #1E3A5F 0%, #14273F 100%);
}

.login-card {
    border-radius: 12px;
}

@media (max-width: 600px) {
    .login-card {
        border-radius: 0;
        min-height: 100vh;
    }
}
</style>